import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-84167edc"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "goal-suggestion-box-container" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "goal-suggestion-header" }, [
  /*#__PURE__*/_createElementVNode("h4", null, "Goal Suggestion")
], -1))
const _hoisted_3 = { class: "goal-suggestion-container" }
const _hoisted_4 = { class: "goal-suggestion-text-box" }
const _hoisted_5 = {
  key: 0,
  "data-test": "goal-suggestion-text"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { class: "prompt-buttons-container" }
const _hoisted_9 = { class: "buttons-centre" }
const _hoisted_10 = { "data-test": "suggestion-count" }
const _hoisted_11 = { class: "buttons-end" }

import { computed, onMounted, ref, watch, watchEffect } from "vue";
import { AxiosError } from "axios";
import LoadingSpinner from "../loading-spinner.vue";
import processPromptVars, { getDataContext } from "../smart-scratchpad/process-prompt-vars";
import { renderErrorMessageToClient } from "../smart-scratchpad/variable-error-helpers";
import chat from "../smart-scratchpad/chat";
import goalCaptureStore from "../../stores/goal-capture-store";
import { Analytics } from "../../services/analytics/analytics";
import { csContext } from "../../services/cs-context";


export default /*#__PURE__*/_defineComponent({
  __name: 'goal-suggestion-box',
  props: {
    goalInput: {},
    prompt: {},
    goalIndex: {}
  },
  emits: ["update:goalSuggestion", "update:goalName"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const text = ref<string>("");
const promptRequestInProgress = ref(false);
const lastPromptIsFinished = ref(true);
const regenerateCount = ref(0);

const goalKey = `goal-${props.goalIndex}`;
const isLoading = computed(() => text.value.length === 0 || promptRequestInProgress.value);
const goalInput = computed(() => props.goalInput);
const goalInputVersions = computed(() => goalCaptureStore.version);
const previousSuggestions = computed(
  () => goalCaptureStore.goalSuggestions[goalKey]?.suggestions ?? "",
);

const emit = __emit;

let version = 0;

let dataContext: any = null;

const previousOutputsPromptText = computed(() => {
  return (
    "\n\nWhen I've recently asked you the prompt that you're about to see, these are the outputs you have produced:" +
    `\n\n['${previousSuggestions.value.join("', '")}']` +
    "\n\nMake sure the next output you produce does not replicate one of the above outputs."
  );
});

const promptWithPrefixing = computed(() => {
  return (
    `\n\nThis number is for tracking, you can ignore this number: ${regenerateCount.value}` +
    `${previousSuggestions.value.length > 0 ? previousOutputsPromptText.value : ""}` +
    "\n\nHere's the prompt that you should generate your response from:" +
    `\n${props.prompt}`
  );
});

function useSuggestion() {
  emit("update:goalName", text.value);
}

function goToPrevSuggestion() {
  text.value = goalCaptureStore.goToPreviousSuggestion(props.goalIndex);
}

function goToNextSuggestion() {
  text.value = goalCaptureStore.goToNextSuggestion(props.goalIndex);
}

async function askPrompt(): Promise<void> {
  const activeVersion = version;
  promptRequestInProgress.value = true;
  lastPromptIsFinished.value = false;

  if (promptWithPrefixing.value?.trim().length > 0) {
    dataContext.reader.active_text = goalInput.value;
    const { processed, errors } = await processPromptVars(promptWithPrefixing.value, dataContext);

    if (errors.length > 0) {
      text.value = renderErrorMessageToClient(errors);
      return;
    }

    Analytics.action("Ran Team Goal Suggestion", "GoalSuggestionActions", {
      teamLeadId: csContext.user.id,
      teamId: csContext.currentTeam?.id,
    });

    chat(processed, (content) => {
      if (activeVersion === version) {
        promptRequestInProgress.value = false;
        text.value = content.trim();
      }
    })
      .then((message) => {
        goalCaptureStore.addGoalSuggestion(text.value, props.goalIndex);
        emit("update:goalSuggestion", text.value);

        Analytics.action("Completed Team Goal Suggestion", "GoalSuggestionActions", {
          teamLeadId: csContext.user.id,
          teamId: csContext.currentTeam?.id,
        });
      })
      .catch((e) => {
        text.value = "Sorry, there was an error. Please refresh the page.";
        const error = e as AxiosError;
        const errorMessage = `${error.response?.status}: ${error.response?.statusText}`;

        Analytics.action("Failed Team Goal Suggestion", "GoalSuggestionActions", {
          error: errorMessage,
          teamId: csContext.currentTeam?.id,
          teamLeadId: csContext.user.id,
        });
      })
      .finally(() => {
        lastPromptIsFinished.value = true;
      });
  }
}

const reRunPrompt = async () => {
  if (lastPromptIsFinished.value) {
    regenerateCount.value += 1;
    await askPrompt();
  }
};

const suggestionHistoryLength = ref(0);
const activeSuggestionIndex = ref(0);

watchEffect(() => {
  if (goalCaptureStore.goalSuggestions[goalKey]) {
    activeSuggestionIndex.value =
      goalCaptureStore.goalSuggestions[goalKey].activeSuggestionIndex ?? 0;
    suggestionHistoryLength.value = goalCaptureStore.goalSuggestions[goalKey].suggestions.length;
    text.value = goalCaptureStore.goalSuggestions[goalKey].suggestions[activeSuggestionIndex.value];
  }
});

watch(goalInputVersions, async () => {
  version += 1;

  if (text.value !== goalInput.value && lastPromptIsFinished.value) {
    await askPrompt();
  }
});

onMounted(async () => {
  dataContext = getDataContext();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        (!isLoading.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(text.value), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              (!promptRequestInProgress.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, "Click 'Run' to generate a suggestion..."))
                : (_openBlock(), _createBlock(LoadingSpinner, {
                    key: 1,
                    type: 'Dots',
                    size: "100px",
                    "data-test": "goal-suggestion-loading"
                  }))
            ]))
      ])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _withDirectives(_createElementVNode("div", _hoisted_9, [
        _createElementVNode("button", {
          class: "btn btn-default-fab-chevron",
          type: "button",
          onClick: goToPrevSuggestion,
          "aria-label": "Previous suggestion",
          "data-test": "previous-suggestion-button"
        }),
        _createElementVNode("span", _hoisted_10, _toDisplayString(activeSuggestionIndex.value + 1) + " / " + _toDisplayString(suggestionHistoryLength.value), 1),
        _createElementVNode("button", {
          class: "btn btn-default-fab-chevron",
          type: "button",
          onClick: goToNextSuggestion,
          "aria-label": "Next suggestion",
          "data-test": "next-suggestion-button"
        })
      ], 512), [
        [_vShow, suggestionHistoryLength.value > 0]
      ]),
      _createElementVNode("div", _hoisted_11, [
        (suggestionHistoryLength.value > 0)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "btn btn-sm btn-default-tick",
              type: "button",
              onClick: useSuggestion,
              "data-test": "use-suggestion-button"
            }, " Use "))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "btn btn-sm btn-default-reload",
          type: "button",
          onClick: reRunPrompt,
          "data-test": "regenerate-suggestion-button"
        }, _toDisplayString(suggestionHistoryLength.value > 0 ? "Regenerate" : "Run"), 1)
      ])
    ])
  ]))
}
}

})